/**
 * RefreshButton Component
 * Reusable refresh/reload button with loading state
 */

import { RefreshCw } from 'lucide-react';
import { ButtonHTMLAttributes } from 'react';

interface RefreshButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    isLoading?: boolean;
    size?: number;
    label?: string;
}

/**
 * RefreshButton - A reusable refresh button with spinning animation
 *
 * @example
 * // Basic usage
 * <RefreshButton onClick={refetch} isLoading={isLoading} />
 *
 * // With label
 * <RefreshButton onClick={refetch} isLoading={isLoading} label="Refresh" />
 */
export function RefreshButton({
    isLoading = false,
    size = 20,
    label,
    className = '',
    disabled,
    ...props
}: RefreshButtonProps) {
    return (
        <button
            type="button"
            disabled={disabled || isLoading}
            className={`
                p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100
                rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                ${label ? 'flex items-center gap-2' : ''}
                ${className}
            `.trim().replace(/\s+/g, ' ')}
            aria-label={label || 'Refresh'}
            {...props}
        >
            <RefreshCw size={size} className={isLoading ? 'animate-spin' : ''} />
            {label && <span className="text-sm">{label}</span>}
        </button>
    );
}

export default RefreshButton;
